// MOCK BACKEND for mentor session booking (Phase 1, student UI only).
//
// Stands in for the FastAPI endpoints in the spec (§5.1/§5.2) and returns the
// same response shapes. It is only reached through services/mentorBookingService.js;
// no page imports this file. Delete it once the real endpoints exist.
//
// Because the real server renders times, so does this file: it is the ONLY place
// that does timezone arithmetic. The UI just prints start_local / date_label.
//
// State lives in localStorage so a page reload keeps your bookings.
// Dev console helpers: window.__mentorBookingMock  (reset(), failNext('slots'))

const STORAGE_KEY = 'mockMentorBooking.v1';
const LATENCY_MS = 350;
const DEFAULT_RANGE_DAYS = 14;
const MAX_RANGE_DAYS = 60;
const LEAD_TIME_MS = 60 * 60 * 1000;
const MINUTE = 60 * 1000;
const DAY = 24 * 60 * MINUTE;

// ---------------------------------------------------------------------------
// Timezone helpers
// ---------------------------------------------------------------------------

const formatterCache = new Map();
const getFormatter = (key, tz, options) => {
  const cacheKey = `${key}|${tz}`;
  if (!formatterCache.has(cacheKey)) {
    formatterCache.set(cacheKey, new Intl.DateTimeFormat('en-US', { timeZone: tz, ...options }));
  }
  return formatterCache.get(cacheKey);
};

const pad = (n) => String(n).padStart(2, '0');

const partsOf = (ms, tz, key, options) => {
  const out = {};
  getFormatter(key, tz, options).formatToParts(new Date(ms)).forEach((p) => {
    out[p.type] = p.value;
  });
  return out;
};

const wallClock = (ms, tz) => partsOf(ms, tz, 'wall', {
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit'
});

const offsetMs = (ms, tz) => {
  const w = wallClock(ms, tz);
  const asUtc = Date.UTC(+w.year, +w.month - 1, +w.day, +w.hour, +w.minute, +w.second);
  return asUtc - Math.floor(ms / 1000) * 1000;
};

// Wall-clock time in `tz` -> UTC ms. Re-checks the offset so DST edges resolve.
const zonedToUtc = (y, m, d, hh, mm, tz) => {
  const guess = Date.UTC(y, m - 1, d, hh, mm);
  const first = guess - offsetMs(guess, tz);
  const second = guess - offsetMs(first, tz);
  return second;
};

const dateKeyIn = (ms, tz) => {
  const w = wallClock(ms, tz);
  return `${w.year}-${w.month}-${w.day}`;
};

const addDays = (dateKey, n) => {
  const [y, m, d] = dateKey.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
};

const daysBetween = (fromKey, toKey) => {
  const [y1, m1, d1] = fromKey.split('-').map(Number);
  const [y2, m2, d2] = toKey.split('-').map(Number);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / DAY);
};

const weekdayOf = (dateKey) => {
  const [y, m, d] = dateKey.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
};

const clockLabel = (ms, tz) => {
  const p = partsOf(ms, tz, 'clock', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${p.hour}:${p.minute} ${p.dayPeriod}`;
};

const dayLabel = (ms, tz) => {
  const p = partsOf(ms, tz, 'dayShort', { weekday: 'short', day: 'numeric', month: 'short' });
  return `${p.weekday}, ${p.day} ${p.month}`;
};

const dayLabelLong = (ms, tz) => {
  const p = partsOf(ms, tz, 'dayLong', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  return `${p.weekday}, ${p.day} ${p.month} ${p.year}`;
};

const parseClock = (hhmm) => hhmm.split(':').map(Number);

// ---------------------------------------------------------------------------
// Mentor fixtures. The mix is deliberate, each one exercises a UI state.
// ---------------------------------------------------------------------------

const svgPhoto = (letter, bg) => `data:image/svg+xml;utf8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect width="200" height="200" fill="${bg}"/><text x="100" y="128" font-size="90" font-family="Arial" font-weight="700" text-anchor="middle" fill="#ffffff">${letter}</text></svg>`
)}`;

const MENTORS = [
  {
    // Plenty of slots, a daily "Google busy" lunch block, has a Meet link.
    id: 'mock-mentor-ananya',
    first_name: 'Ananya',
    last_name: 'Rao',
    expertise: 'Machine Learning',
    bio: 'Former research engineer who now mentors students on applied ML projects, from picking a dataset to presenting results. Happy to review code, scope documents and competition entries.',
    photo_url: null,
    session_duration_minutes: 60,
    buffer_minutes: 10,
    timezone: 'Asia/Kolkata',
    rules: [{ days: [1, 2, 3, 4, 5, 6], start: '10:00', end: '17:00' }],
    busy: [{ start: '12:00', end: '13:30' }],
    calendar_synced: true,
    meet: true
  },
  {
    // Sparse week, 45 min sessions, no photo, part-time (no Meet link). Its first slot is
    // "contested": booking it the first time returns 409 so the taken-slot path can be tested.
    id: 'mock-mentor-rohan',
    first_name: 'Rohan',
    last_name: 'Mehta',
    expertise: 'Robotics & Embedded Systems',
    bio: 'Builds hardware prototypes for a living. Works best with students who have a half-built circuit and a deadline.',
    photo_url: null,
    session_duration_minutes: 45,
    buffer_minutes: 15,
    timezone: 'Asia/Kolkata',
    rules: [{ days: [2, 4], start: '15:00', end: '18:00' }],
    busy: [],
    calendar_synced: true,
    meet: false,
    contestedFirstSlot: true
  },
  {
    // Different timezone, no bio, and the calendar refresh failed (banner).
    id: 'mock-mentor-sarah',
    first_name: 'Sarah',
    last_name: 'Collins',
    expertise: 'Research Methods & Academic Writing',
    bio: '',
    photo_url: null,
    session_duration_minutes: 30,
    buffer_minutes: 10,
    timezone: 'Europe/London',
    rules: [{ days: [1, 2, 3], start: '09:00', end: '13:00' }],
    busy: [],
    calendar_synced: false,
    meet: true
  },
  {
    // Has a photo, 60 minute sessions, evenings in Singapore.
    id: 'mock-mentor-vikram',
    first_name: 'Vikram',
    last_name: 'Iyer',
    expertise: 'Web Development',
    bio: 'Full-stack developer. Pair-programming style sessions: bring a bug or a feature and we will work through it together.',
    photo_url: svgPhoto('V', '#2196F3'),
    session_duration_minutes: 60,
    buffer_minutes: 10,
    timezone: 'Asia/Singapore',
    rules: [{ days: [1, 2, 3, 4, 5], start: '18:00', end: '21:30' }],
    busy: [],
    calendar_synced: true,
    meet: true
  },
  {
    // Weekly window shorter than one session: bookable, but no chips (the empty state).
    id: 'mock-mentor-tarun',
    first_name: 'Tarun',
    last_name: 'Bose',
    expertise: 'Data Visualisation',
    bio: 'Currently has very limited availability.',
    photo_url: null,
    session_duration_minutes: 30,
    buffer_minutes: 10,
    timezone: 'Asia/Kolkata',
    rules: [{ days: [1], start: '10:00', end: '10:20' }],
    busy: [],
    calendar_synced: true,
    meet: false
  },
  {
    // No rules and no calendar: must NOT appear in the mentor list.
    id: 'mock-mentor-hidden',
    first_name: 'Hidden',
    last_name: 'Mentor',
    expertise: 'Should not be listed',
    bio: 'No availability configured.',
    photo_url: null,
    session_duration_minutes: 30,
    buffer_minutes: 10,
    timezone: 'Asia/Kolkata',
    rules: [],
    busy: [],
    calendar_synced: true,
    meet: false,
    hasCalendar: false
  }
];

const isBookable = (m) => m.rules.length > 0 || m.hasCalendar === true;

// Production scopes this to the logged-in student's own mentor (server-side). The mock
// mirrors that: the "student" belongs to Ananya, and every other mentor is invisible and
// unbookable. For testing the other fixtures: __mentorBookingMock.showAllMentors(true).
const STUDENT_MENTOR_ID = 'mock-mentor-ananya';
const SHOW_ALL_KEY = 'mockMentorBooking.showAllMentors';

const showAllMentors = () => {
  try {
    return localStorage.getItem(SHOW_ALL_KEY) === '1';
  } catch (e) {
    return false;
  }
};

const isVisibleToStudent = (m) => isBookable(m) && (showAllMentors() || m.id === STUDENT_MENTOR_ID);

// The student's projects with each mentor. Ananya has two, so the confirm step shows the project
// picker for her; everyone else has one (nothing to choose, shown as a plain summary row).
const PROJECTS_BY_MENTOR = {
  'mock-mentor-ananya': [
    { id: 'mock-project-ml', project_name: 'Crop Disease Detection' },
    { id: 'mock-project-nlp', project_name: 'Sentiment Analysis for Reviews' }
  ]
};
const defaultProjects = (m) => [{ id: `mock-project-${m.id}`, project_name: `${m.first_name}'s project` }];
const projectsFor = (m) => PROJECTS_BY_MENTOR[m.id] || defaultProjects(m);

const publicMentor = (m) => ({
  id: m.id,
  projects: projectsFor(m),
  first_name: m.first_name,
  last_name: m.last_name,
  photo_url: m.photo_url,
  expertise: m.expertise,
  bio: m.bio,
  session_duration_minutes: m.session_duration_minutes,
  buffer_minutes: m.buffer_minutes,
  timezone: m.timezone
});

const findMentor = (id) => MENTORS.find((m) => m.id === id);

// ---------------------------------------------------------------------------
// Slot generator (mirrors spec §5.3, minus Google and overrides)
// ---------------------------------------------------------------------------

const ACTIVE = (s) => s.status !== 'cancelled' && s.status !== 'rescheduled';

const computeSlotsUtc = (mentor, { dateFrom, dateTo }, sessions) => {
  const dur = mentor.session_duration_minutes * MINUTE;
  const buf = mentor.buffer_minutes * MINUTE;
  const now = Date.now();
  const slots = [];

  const booked = sessions.filter((s) => s.mentor_id === mentor.id && ACTIVE(s));
  const taken = state.taken.filter((t) => t.mentor_id === mentor.id);

  for (let key = dateFrom; key <= dateTo; key = addDays(key, 1)) {
    const [y, m, d] = key.split('-').map(Number);
    const weekday = weekdayOf(key);
    mentor.rules.filter((r) => r.days.includes(weekday)).forEach((rule) => {
      const [sh, sm] = parseClock(rule.start);
      const [eh, em] = parseClock(rule.end);
      const windowStart = zonedToUtc(y, m, d, sh, sm, mentor.timezone);
      const windowEnd = zonedToUtc(y, m, d, eh, em, mentor.timezone);
      const busy = mentor.busy.map((b) => {
        const [bsh, bsm] = parseClock(b.start);
        const [beh, bem] = parseClock(b.end);
        return [zonedToUtc(y, m, d, bsh, bsm, mentor.timezone), zonedToUtc(y, m, d, beh, bem, mentor.timezone)];
      });

      for (let cur = windowStart; cur + dur <= windowEnd; cur += dur + buf) {
        const end = cur + dur;
        if (cur < now + LEAD_TIME_MS) continue;
        if (busy.some(([bs, be]) => cur < be && end + buf > bs)) continue;
        if (booked.some((s) => cur < Date.parse(s.end_time_utc) + buf && end > Date.parse(s.start_time_utc))) continue;
        if (taken.some((t) => t.start_ms === cur)) continue;
        slots.push({ start: cur, end });
      }
    });
  }
  return slots.sort((a, b) => a.start - b.start);
};

const rangeFor = (mentor, dateFrom, dateTo) => {
  const today = dateKeyIn(Date.now(), mentor.timezone);
  const from = dateFrom && dateFrom > today ? dateFrom : today;
  let to = dateTo || addDays(from, DEFAULT_RANGE_DAYS);
  if (daysBetween(from, to) > MAX_RANGE_DAYS) to = addDays(from, MAX_RANGE_DAYS);
  return { dateFrom: from, dateTo: to };
};

// ---------------------------------------------------------------------------
// Persistent state
// ---------------------------------------------------------------------------

let state = null;
let seq = 0;
const nextId = () => `mock-session-${Date.now().toString(36)}-${++seq}`;
const MEET_LINK = 'https://meet.google.com/mock-demo-room';

const buildSession = (mentor, startMs, overrides = {}) => ({
  id: nextId(),
  mentor_id: mentor.id,
  start_time_utc: new Date(startMs).toISOString(),
  end_time_utc: new Date(startMs + mentor.session_duration_minutes * MINUTE).toISOString(),
  duration_minutes: mentor.session_duration_minutes,
  timezone_booked: 'Asia/Kolkata',
  status: 'scheduled',
  notes: null,
  // The real server derives this from the mentor's email domain; the mock mirrors it from the
  // fixture's `meet` flag so the student confirmation message can be exercised both ways.
  booking_gateway: mentor.meet ? 'internal' : 'external',
  class_session_id: null,
  meet_link: mentor.meet ? MEET_LINK : null,
  cancelled_at: null,
  cancellation_reason: null,
  rescheduled_from: null,
  created_at: new Date().toISOString(),
  ...overrides
});

const seed = () => {
  state = { sessions: [], taken: [], contestedUsed: [] };
  const now = Date.now();
  const range = (m) => rangeFor(m, null, null);
  const ananya = findMentor('mock-mentor-ananya');
  const sarah = findMentor('mock-mentor-sarah');

  // A couple of bookings already on the books, so their slots are visibly missing from the picker.
  const futureSlots = (m) => computeSlotsUtc(m, range(m), []).filter((s) => s.start > now + 2 * DAY);
  const aSlots = futureSlots(ananya);
  const sSlots = futureSlots(sarah);

  if (aSlots[2]) state.sessions.push(buildSession(ananya, aSlots[2].start));
  if (sSlots[3]) state.sessions.push(buildSession(sarah, sSlots[3].start, { timezone_booked: 'Europe/London' }));
};

const save = () => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    // storage unavailable: the mock still works for this page load
  }
};

const ensureState = () => {
  if (state) return;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      state = JSON.parse(raw);
      return;
    }
  } catch (e) {
    // fall through to seeding
  }
  seed();
  save();
};

// ---------------------------------------------------------------------------
// Dev hooks + plumbing
// ---------------------------------------------------------------------------

let failNextKey = null;

if (typeof window !== 'undefined') {
  window.__mentorBookingMock = {
    // failNext('mentors' | 'slots' | 'book'): the next call of that kind returns a 500.
    failNext: (key) => { failNextKey = key; },
    // showAllMentors(true): list every fixture mentor (Rohan's 409, Sarah's banner, Tarun's empty
    // state...). showAllMentors(false): back to production behaviour, only the student's mentor.
    showAllMentors: (on = true) => {
      try {
        if (on) localStorage.setItem(SHOW_ALL_KEY, '1');
        else localStorage.removeItem(SHOW_ALL_KEY);
      } catch (e) { /* ignore */ }
      return on ? 'All mock mentors visible. Reload the page.' : 'Only the student\'s own mentor. Reload the page.';
    },
    // Frees every booked slot (and the contested one). reset() restores the demo data.
    clearBookings: () => {
      ensureState();
      state.sessions = [];
      state.taken = [];
      save();
      return 'All mock bookings removed. Reload the page.';
    },
    reset: () => {
      state = null;
      try { localStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
      ensureState();
      return 'Mock bookings reset. Reload the page.';
    }
  };
}

const delay = () => new Promise((resolve) => setTimeout(resolve, LATENCY_MS));

// Same shape an axios error has, so pages treat mock and real failures alike.
const httpError = (status, detail) => {
  const err = new Error(detail);
  err.response = { status, data: { detail } };
  return err;
};

const maybeFail = (key) => {
  if (failNextKey === key) {
    failNextKey = null;
    throw httpError(500, 'Mock server error');
  }
};

// ---------------------------------------------------------------------------
// Public API (same names as the real service)
// ---------------------------------------------------------------------------

export const getBookableMentors = async () => {
  await delay();
  maybeFail('mentors');
  return MENTORS.filter(isVisibleToStudent).map(publicMentor);
};

export const getMentorSlots = async (mentorId, { timezone = 'Asia/Kolkata', dateFrom, dateTo } = {}) => {
  await delay();
  maybeFail('slots');
  ensureState();
  const mentor = findMentor(mentorId);
  if (!mentor || !isVisibleToStudent(mentor)) throw httpError(404, 'Mentor not found');

  const range = rangeFor(mentor, dateFrom, dateTo);
  const slots = computeSlotsUtc(mentor, range, state.sessions);

  const byDay = new Map();
  slots.forEach((s) => {
    const key = dateKeyIn(s.start, timezone);
    if (!byDay.has(key)) {
      byDay.set(key, {
        date: key,
        date_label: dayLabel(s.start, timezone),
        date_long: dayLabelLong(s.start, timezone),
        slots: []
      });
    }
    byDay.get(key).slots.push({
      start_utc: new Date(s.start).toISOString().replace('.000Z', 'Z'),
      end_utc: new Date(s.end).toISOString().replace('.000Z', 'Z'),
      start_local: clockLabel(s.start, timezone),
      end_local: clockLabel(s.end, timezone)
    });
  });

  return {
    mentor: publicMentor(mentor),
    timezone,
    calendar_synced: mentor.calendar_synced,
    days: [...byDay.values()].sort((a, b) => (a.date < b.date ? -1 : 1))
  };
};

// Is `startUtc` really one of the mentor's open slots? Throws 422 / 409 if not.
const assertSlotOpen = (mentor, startUtc) => {
  const startMs = Date.parse(startUtc);
  if (Number.isNaN(startMs)) throw httpError(422, 'Invalid start time.');
  if (startMs < Date.now() + LEAD_TIME_MS) {
    throw httpError(422, 'Sessions must be booked at least 1 hour ahead.');
  }

  // Contested slot: someone "else" grabs it the first time it is tried.
  if (mentor.contestedFirstSlot && !state.contestedUsed.includes(mentor.id)) {
    const first = computeSlotsUtc(mentor, rangeFor(mentor, null, null), state.sessions)[0];
    if (first && first.start === startMs) {
      state.contestedUsed.push(mentor.id);
      state.taken.push({ mentor_id: mentor.id, start_ms: startMs });
      save();
      throw httpError(409, 'That slot is no longer available.');
    }
  }

  if (state.taken.some((t) => t.mentor_id === mentor.id && t.start_ms === startMs)) {
    throw httpError(409, 'That slot is no longer available.');
  }

  const open = computeSlotsUtc(mentor, rangeFor(mentor, null, null), state.sessions)
    .some((s) => s.start === startMs);
  if (!open) {
    // Distinguish "someone took it" (409) from "never was a slot" (422), as the real server will.
    const wasEverASlot = computeSlotsUtc(mentor, rangeFor(mentor, null, null), [])
      .some((s) => s.start === startMs);
    if (wasEverASlot) throw httpError(409, 'That slot is no longer available.');
    throw httpError(422, "That time is outside the mentor's availability.");
  }
  return startMs;
};

export const bookSession = async ({ mentor_id, project_id, start_time_utc, timezone, notes } = {}) => {
  await delay();
  maybeFail('book');
  ensureState();
  const mentor = findMentor(mentor_id);
  if (!mentor || !isVisibleToStudent(mentor)) throw httpError(404, 'Mentor not found');
  if (notes && notes.length > 500) throw httpError(422, 'Notes must be 500 characters or fewer.');

  // Same rules as the server: optional only when there is exactly one project to pick.
  const projects = projectsFor(mentor);
  let chosenProject = project_id;
  if (chosenProject) {
    if (!projects.some((p) => p.id === chosenProject)) {
      throw httpError(422, 'Choose one of your projects with this mentor.');
    }
  } else if (projects.length === 1) {
    chosenProject = projects[0].id;
  } else {
    throw httpError(422, 'Choose which project this session is for.');
  }

  const startMs = assertSlotOpen(mentor, start_time_utc);
  const session = buildSession(mentor, startMs, {
    project_id: chosenProject,
    timezone_booked: timezone || 'Asia/Kolkata',
    notes: notes || null
  });
  state.sessions.push(session);
  save();
  return session;
};

